import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getPresentations, createPresentation, updatePresentation, deletePresentation } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, PresentationChartBarIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

const STATUS_COLORS = {
  upcoming: 'bg-blue-900/50 text-blue-300',
  completed: 'bg-green-900/50 text-green-300',
  cancelled: 'bg-red-900/50 text-red-300',
};

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-lg">
        {children}
      </div>
    </div>
  );
}

export default function PresentationsPage() {
  const { user, isFellow, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ title: '', presentation_date: '', venue: '', notes: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['presentations', filter],
    queryFn: () => getPresentations({ status: filter || undefined }),
  });

  const createMut = useMutation({
    mutationFn: createPresentation,
    onSuccess: () => { qc.invalidateQueries(['presentations']); toast.success('Presentation scheduled!'); setShowModal(false); },
    onError: () => toast.error('Failed to create presentation'),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => updatePresentation(id, data),
    onSuccess: () => { qc.invalidateQueries(['presentations']); toast.success('Updated!'); setEditItem(null); },
  });

  const deleteMut = useMutation({
    mutationFn: deletePresentation,
    onSuccess: () => { qc.invalidateQueries(['presentations']); toast.success('Deleted'); },
  });

  const presentations = data?.data || [];

  const handleSubmit = (e) => {
    e.preventDefault();
    createMut.mutate({ ...form, presentation_date: new Date(form.presentation_date).toISOString() });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Presentations</h1>
          <p className="text-gray-500 text-sm">Track and manage MSc presentations</p>
        </div>
        {isFellow && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> Schedule
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'upcoming', 'completed', 'cancelled'].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === s ? 'bg-primary-600 text-white' : 'bg-surface-100 text-gray-400 hover:text-white'}`}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : presentations.length === 0 ? (
        <div className="card text-center py-12">
          <PresentationChartBarIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No presentations found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {presentations.map((p) => (
            <div key={p.id} className="card flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white text-sm">{p.title}</span>
                  <span className={`badge ${STATUS_COLORS[p.status]}`}>{p.status}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {p.presenter?.full_name} · {format(new Date(p.presentation_date), 'dd MMM yyyy, HH:mm')}
                  {p.venue && ` · ${p.venue}`}
                </div>
                {p.notes && <div className="text-xs text-gray-600 mt-1">{p.notes}</div>}
                {p.slides_url && (
                  <a href={p.slides_url} target="_blank" rel="noreferrer" className="text-xs text-primary-400 hover:underline mt-1 inline-block">
                    View Slides
                  </a>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {(p.presenter?.id === user?.id || isAdmin) && p.status === 'upcoming' && (
                  <>
                    <button
                      onClick={() => updateMut.mutate({ id: p.id, data: { status: 'completed' } })}
                      className="text-xs text-green-400 hover:text-green-300 bg-green-900/20 px-2 py-1 rounded"
                    >
                      Mark Done
                    </button>
                    {isAdmin && (
                      <button onClick={() => { if (window.confirm('Delete?')) deleteMut.mutate(p.id); }} className="p-1.5 text-red-400 hover:text-red-300">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Schedule Presentation</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Date & Time *</label>
                <input type="datetime-local" className="input" required value={form.presentation_date} onChange={(e) => setForm((f) => ({ ...f, presentation_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Venue</label>
                <input className="input" value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Notes</label>
                <textarea className="input resize-none" rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>
                  {createMut.isPending ? 'Scheduling...' : 'Schedule'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
